import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('agri_token'));
  const [loading, setLoading] = useState(true);

  // Set axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const fetchMe = useCallback(async (tok) => {
    try {
      const t = tok || token;
      if (!t) { setLoading(false); return; }
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      setToken(null);
      setUser(null);
      localStorage.removeItem('agri_token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMe();
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = res.data;
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('agri_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return userData;
  };

  const register = async (email, password, name, tenantName, tenantSlug) => {
    // Legacy: kept for backward compatibility but now goes through request-access
    await axios.post(`${API}/auth/request-access`, {
      email, password, name, tenant_name: tenantName, tenant_slug: tenantSlug,
    });
    // Don't auto-login — request is pending approval
  };

  const requestAccess = async (email, password, name, tenantName, tenantSlug, requestType = 'join_tenant') => {
    await axios.post(`${API}/auth/request-access`, {
      email, password, name,
      tenant_name: tenantName,
      tenant_slug: tenantSlug,
      request_type: requestType,
    });
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('agri_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const hasRole = (...roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const canAccess = (minRole) => {
    const hierarchy = ['super_admin', 'admin', 'sales_manager', 'sales_rep', 'operator', 'client'];
    const userIdx = hierarchy.indexOf(user?.role || '');
    const minIdx = hierarchy.indexOf(minRole);
    return userIdx !== -1 && userIdx <= minIdx;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, requestAccess, logout, hasRole, canAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
